import makeStyles from '@material-ui/core/styles/makeStyles';
import classnames from 'classnames';
import { forwardRef, useMemo, useState } from 'react';
import { color } from '../../app/theme';
import Loading from './Loading';
import { Cell, Row, StickyTable } from './StickyTable';

const useStyles = makeStyles(
  {
    headerCell: {
      backgroundColor: color.$8,
      height: 44,
      fontWeight: '500',
      fontSize: '13px',
      lineHeight: '18px',
      color: color.$12,
      padding: '0 8px',
      verticalAlign: 'middle',
      '& > div': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
      },
    },
    rowCell: {
      fontWeight: '400',
      fontSize: '15px',
      lineHeight: '15px',
      color: color.$13,
      verticalAlign: 'middle',
      padding: '12px 8px',
      backgroundColor: color.$7,
    },
    loading: {
      margin: '110px 0',
    },
  },
  {
    name: 'Table',
  },
);

function RowComponent({ data, index, onClickRow, dataKeys, classNames }) {
  const classes = useStyles();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Row
      className={classNames.row}
      onClick={() => onClickRow(data)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {(dataKeys || []).map((key, cellIndex) => {
        return (
          <Cell
            key={`row-${index}-cell-${cellIndex}`}
            className={classnames(classes.rowCell, classNames.rowCell)}
          >
            {typeof data?.[key] === 'function'
              ? data?.[key](data, isHovered)
              : data?.[key]}
          </Cell>
        );
      })}
    </Row>
  );
}

/**
 *
 * @param {content<JSX, String, Number>, key, <DivProps>} columns<Array>
 * @returns
 *
 * @param {key: value<JSX, String, Number>} data<Object>
 * @returns
 *
 */
const Table = forwardRef(
  (
    {
      id,
      style,
      headerZ = 2,
      leftColumnZ = 2,
      rightColumnZ = 2,
      footerZ = 2,
      leftStickyColumnCount = 0,
      stickyHeaderCount = 0,
      stickyFooterCount = 0,
      rightStickyColumnCount = 0,
      isColumnsAutoSize = false,
      onClickRow = () => {},
      columns = [],
      data = [],
      classNames = {
        header: '',
        row: '',
        rowCell: '',
        table: '',
        loading: '',
        wrapper: '',
      },
      isLoading,
      emptyComponent,
    },
    ref,
  ) => {
    const classes = useStyles();

    const dataKeys = useMemo(() => {
      return columns.map(c => c?.key) || [];
    }, [columns]);

    const headerComponents = useMemo(() => {
      return columns.map((c, i) => {
        const { key, content, className, ...headerProps } = c || {};
        return (
          <Cell
            key={key || `header-${i}`}
            className={classnames(classes.headerCell, className)}
            {...headerProps}
          >
            {content}
          </Cell>
        );
      });
    }, [columns]);

    const dataComponents = useMemo(() => {
      return data.map((d, i) => {
        return (
          <RowComponent
            key={`eric-row-${i}`}
            data={d}
            index={i}
            onClickRow={onClickRow}
            dataKeys={dataKeys}
            classNames={classNames}
          />
        );
      });
    }, [data, dataKeys]);

    return (
      <div className={classNames.root} style={style}>
        <StickyTable
          id={id}
          ref={ref}
          className={classNames.wrapper}
          classNames={classNames}
          leftStickyColumnCount={leftStickyColumnCount}
          stickyHeaderCount={stickyHeaderCount}
          stickyFooterCount={stickyFooterCount}
          rightStickyColumnCount={rightStickyColumnCount}
          isColumnsAutoSize={isColumnsAutoSize}
          headerZ={headerZ}
          leftColumnZ={leftColumnZ}
          rightColumnZ={rightColumnZ}
          footerZ={footerZ}
          style={{
            overflow:
              isLoading || (!data?.length && emptyComponent)
                ? 'hidden'
                : 'auto',
          }}
        >
          <Row className={classNames.header}>{headerComponents}</Row>
          {!isLoading && dataComponents}
        </StickyTable>
        {isLoading && (
          <div
            className={classnames(classes.loading, classNames?.loading || '')}
          >
            <Loading />
          </div>
        )}
        {!isLoading && !data?.length && emptyComponent}
      </div>
    );
  },
);

export default Table;
